import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet, apiPut } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

const EXTRA_FIELDS = Array.from({ length: 20 }, (_, i) => i + 1);

const CORE_FIELDS = [
  { key: "name", labelKey: "nameLabel", requiredKey: "nameRequired", defaultLabel: "Name" },
  { key: "mobileNo", labelKey: "mobileNoLabel", requiredKey: "mobileNoRequired", defaultLabel: "Mobile No" },
  { key: "emailId", labelKey: "emailIdLabel", requiredKey: "emailIdRequired", defaultLabel: "Email Id" },
  { key: "address", labelKey: "addressLabel", requiredKey: "addressRequired", defaultLabel: "Address" },
  { key: "subject", labelKey: "subjectLabel", requiredKey: "subjectRequired", defaultLabel: "Subject" },
];

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function emptyConfig(customerId) {
  const base = {
    customerId: Number(customerId) || 0,
    nameLabel: "Name",
    nameRequired: true,
    mobileNoLabel: "Mobile No",
    mobileNoRequired: true,
    emailIdLabel: "Email Id",
    emailIdRequired: true,
    addressLabel: "Address",
    addressRequired: false,
    subjectLabel: "Subject",
    subjectRequired: false,
  };
  EXTRA_FIELDS.forEach((n) => {
    base[`field${n}Label`] = "";
    base[`field${n}Enabled`] = false;
    base[`field${n}Required`] = false;
  });
  return base;
}

export function RegistrationsPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [tab, setTab] = useState("submissions");
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(100);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  const effectiveCustomerId = useMemo(
    () => (isAdmin ? selectedCustomerId : String(user?.customerId || "")),
    [isAdmin, selectedCustomerId, user]
  );

  const queryCustomer = useMemo(
    () => (isAdmin && selectedCustomerId ? `customerId=${encodeURIComponent(selectedCustomerId)}` : ""),
    [isAdmin, selectedCustomerId]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) {
        const list = Array.isArray(res.data) ? res.data : [];
        setCustomers(list);
        if (!selectedCustomerId && list[0]?.id) setSelectedCustomerId(String(list[0].id));
      }
    };
    void loadCustomers();
  }, [isAdmin, selectedCustomerId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    const parts = [`limit=${encodeURIComponent(limit)}`];
    if (queryCustomer) parts.push(queryCustomer);
    if (search.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);
    const res = await apiGet(`/api/Registrations?${parts.join("&")}`);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load registrations"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [limit, queryCustomer, search, toast]);

  useEffect(() => {
    if (tab === "submissions") void loadRows();
  }, [loadRows, tab]);

  useEffect(() => {
    if (!selected) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelected(null);
    };
    window.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [selected]);

  const loadConfig = useCallback(async () => {
    if (!effectiveCustomerId) return;
    setConfigLoading(true);
    const q = `?customerId=${encodeURIComponent(effectiveCustomerId)}`;
    const res = await apiGet(`/api/Registrations/form-config${q}`);
    setConfigLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load form settings"), "error");
      setConfig(emptyConfig(effectiveCustomerId));
      return;
    }
    setConfig({
      ...emptyConfig(effectiveCustomerId),
      ...(res.data || {}),
      customerId: Number(effectiveCustomerId),
    });
  }, [effectiveCustomerId, toast]);

  useEffect(() => {
    if (tab === "settings") void loadConfig();
  }, [loadConfig, tab]);

  const deleteRow = async (row) => {
    if (!window.confirm(`Delete registration for ${row.name || row.emailId || `#${row.id}`}?`)) return;
    const res = await apiDelete(`/api/Registrations/${row.id}`);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to delete"), "error");
      return;
    }
    toast.push("Registration deleted", "success");
    if (selected?.id === row.id) setSelected(null);
    await loadRows();
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    if (!config || !effectiveCustomerId) return;
    setSavingConfig(true);
    const payload = { ...config, customerId: Number(effectiveCustomerId) };
    const res = await apiPut("/api/Registrations/form-config", payload);
    setSavingConfig(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to save form settings"), "error");
      return;
    }
    setConfig({
      ...emptyConfig(effectiveCustomerId),
      ...(res.data || {}),
      customerId: Number(effectiveCustomerId),
    });
    toast.push("Form settings saved", "success");
  };

  const patchConfig = (key, value) => {
    setConfig((current) => ({ ...(current || emptyConfig(effectiveCustomerId)), [key]: value }));
  };

  const enabledExtraColumns = useMemo(() => {
    if (!config) return [];
    return EXTRA_FIELDS.filter((n) => config[`field${n}Enabled`]).map((n) => ({
      n,
      label: config[`field${n}Label`] || `Field ${n}`,
      key: `field${n}`,
    }));
  }, [config]);

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Registrations</h1>
            <p className="mt-1 text-sm text-slate-500">
              Website registrations with configurable extra fields (Field 1–20).
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setTab("submissions")}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                tab === "submissions"
                  ? "bg-brand-600 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Submissions
            </button>
            <button
              type="button"
              onClick={() => setTab("settings")}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                tab === "settings"
                  ? "bg-brand-600 text-white"
                  : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
              }`}
            >
              Form settings
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {isAdmin && (
            <select
              className={inputClass}
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
          {tab === "submissions" && (
            <>
              <input
                className={`${inputClass} min-w-[220px]`}
                placeholder="Search name, mobile, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <select
                className={inputClass}
                value={String(limit)}
                onChange={(e) => setLimit(Number(e.target.value) || 100)}
              >
                <option value="50">50 rows</option>
                <option value="100">100 rows</option>
                <option value="200">200 rows</option>
                <option value="500">500 rows</option>
              </select>
              <button
                type="button"
                onClick={() => void loadRows()}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Refresh
              </button>
            </>
          )}
        </div>
      </div>

      {tab === "submissions" ? (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[980px] text-left text-sm">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Register date</th>
                    {isAdmin && <th className="px-4 py-3 text-xs font-semibold text-slate-500">Customer</th>}
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Contact</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Subject</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Email</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.map((row) => (
                    <tr
                      key={row.id}
                      className="cursor-pointer hover:bg-slate-50/80"
                      onClick={() => setSelected(row)}
                    >
                      <td className="px-4 py-3 text-slate-600">{formatDateTime(row.registerDate)}</td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-slate-600">
                          {row.customerName || row.customerCode || "-"}
                        </td>
                      )}
                      <td className="px-4 py-3 font-medium text-slate-900">{row.name || "-"}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <p>{row.mobileNo || "-"}</p>
                        <p className="text-xs text-slate-500">{row.emailId || ""}</p>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{row.subject || "-"}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold ${
                            row.emailSent
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-50 text-amber-700"
                          }`}
                        >
                          {row.emailSent ? "Sent" : "Not sent"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelected(row);
                            }}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              void deleteRow(row);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={isAdmin ? 7 : 6}
                        className="px-4 py-12 text-center text-sm text-slate-500"
                      >
                        No registrations found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
          {!effectiveCustomerId ? (
            <p className="text-sm text-slate-500">Select a customer to configure the registration form.</p>
          ) : configLoading || !config ? (
            <p className="text-sm text-slate-500">Loading form settings...</p>
          ) : (
            <form onSubmit={saveConfig} className="space-y-8">
              <section>
                <h2 className="text-base font-semibold text-slate-900">Core fields</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Always available on the form. Set labels and required flags.
                </p>
                <div className="mt-4 space-y-3">
                  {CORE_FIELDS.map((field) => (
                    <div
                      key={field.key}
                      className="grid gap-3 rounded-xl border border-slate-200 p-4 sm:grid-cols-[1fr_auto]"
                    >
                      <label className="block text-sm">
                        <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Label
                        </span>
                        <input
                          className={`${inputClass} w-full`}
                          value={config[field.labelKey] || ""}
                          onChange={(e) => patchConfig(field.labelKey, e.target.value)}
                          placeholder={field.defaultLabel}
                        />
                      </label>
                      <label className="flex items-end gap-2 pb-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={Boolean(config[field.requiredKey])}
                          onChange={(e) => patchConfig(field.requiredKey, e.target.checked)}
                        />
                        Required
                      </label>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-base font-semibold text-slate-900">Extra fields (Field 1–20)</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Enable fields to show them on the website form. Required only applies when enabled.
                </p>
                <div className="mt-4 space-y-3">
                  {EXTRA_FIELDS.map((n) => {
                    const enabled = Boolean(config[`field${n}Enabled`]);
                    return (
                      <div
                        key={n}
                        className="grid gap-3 rounded-xl border border-slate-200 p-4 md:grid-cols-[88px_1fr_auto_auto]"
                      >
                        <div className="flex items-center text-sm font-semibold text-slate-700">
                          Field {n}
                        </div>
                        <input
                          className={`${inputClass} w-full`}
                          value={config[`field${n}Label`] || ""}
                          onChange={(e) => patchConfig(`field${n}Label`, e.target.value)}
                          placeholder={`Label for Field ${n}`}
                          disabled={!enabled}
                        />
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={enabled}
                            onChange={(e) => {
                              patchConfig(`field${n}Enabled`, e.target.checked);
                              if (!e.target.checked) patchConfig(`field${n}Required`, false);
                            }}
                          />
                          Enable
                        </label>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(config[`field${n}Required`])}
                            disabled={!enabled}
                            onChange={(e) => patchConfig(`field${n}Required`, e.target.checked)}
                          />
                          Required
                        </label>
                      </div>
                    );
                  })}
                </div>
              </section>

              {enabledExtraColumns.length > 0 && (
                <p className="text-sm text-slate-500">
                  Currently enabled: {enabledExtraColumns.map((f) => f.label).join(", ")}
                </p>
              )}

              <button
                type="submit"
                disabled={savingConfig}
                className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {savingConfig ? "Saving..." : "Save form settings"}
              </button>
            </form>
          )}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="presentation">
          <button
            type="button"
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setSelected(null)}
            aria-label="Close"
          />
          <div
            className="relative z-10 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="registration-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 sm:px-6">
              <div>
                <h2 id="registration-detail-title" className="text-lg font-semibold text-slate-900">
                  Registration details
                </h2>
                <p className="mt-1 text-sm text-slate-500">{formatDateTime(selected.registerDate)}</p>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-4 sm:px-6">
              <dl className="grid gap-4 sm:grid-cols-2">
                <Detail label="Name" value={selected.name} />
                <Detail label="Mobile No" value={selected.mobileNo} />
                <Detail label="Email Id" value={selected.emailId} />
                <Detail label="Subject" value={selected.subject} />
                <Detail label="Address" value={selected.address} className="sm:col-span-2" />
                <Detail label="Page URL" value={selected.pageUrl} className="sm:col-span-2" />
                <Detail
                  label="Email notification"
                  value={
                    selected.emailSent
                      ? "Sent"
                      : selected.emailError
                        ? `Not sent — ${selected.emailError}`
                        : "Not sent"
                  }
                  className="sm:col-span-2"
                />
                {EXTRA_FIELDS.map((n) => {
                  const value = selected[`field${n}`];
                  if (!value) return null;
                  return <Detail key={n} label={`Field ${n}`} value={value} />;
                })}
              </dl>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-4 sm:px-6">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => void deleteRow(selected)}
                className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value, className = "" }) {
  return (
    <div className={className}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 whitespace-pre-wrap text-slate-800">{value || "-"}</dd>
    </div>
  );
}
